import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Row, Col, Select, Typography, Tooltip } from 'antd';
import TextArea from '../../forms/common/inputs/textarea';
import moment, { Moment } from 'moment';
import { SessionBoundModel } from 'redux-orm';
import { Contact } from '@copilot/common/store/models/redux';
import { useRedirectToTemplate } from '@copilot/common/hooks/common';
import { arrayToMap, disablePrevDates } from '@copilot/common/utils';
import { ReminderDateOptions } from '@copilot/common/utils/dateUtil';
import ReminderSelector from '../../selector/reminder';
import { ProspectDrawerTrackingParams } from '../../drawer/wrappers/contact/tracking';
import { CampaignDashboardTabKeys } from '@copilot/common/utils/campaign/dashboardTabs';
import { CopilotDatePicker } from '../../input/dates';

const { Option } = Select;
const { Text } = Typography;

type TemplateOptionType = {
	value: string;
	label: string;
	id: string;
};

type SubmitFormProps = {
	submitText: string;
	selectedDate?: moment.Moment | null;
	onSetDate?: (date: moment.Moment | null) => void;
	onSubmit?: (x: string, templateId?: string) => unknown;
	onSetReminderDate?: (date: Moment) => void;
	onSetReminder?: (date: Moment) => void;
	disabled?: boolean;
	disableText?: boolean;
	templateOptions?: TemplateOptionType[];
	templateLabel?: string;
	value?: string;
	contact?: SessionBoundModel<Contact> | null;
	onTracking?: (params: ProspectDrawerTrackingParams) => void;
};

type FormValues = {
	message: string;
	templateId: string;
	dateSelected: moment.Moment | null | undefined;
};

function SubmitForm({
	submitText,
	selectedDate,
	onSetDate,
	onSetReminderDate,
	onSetReminder,
	onSubmit,
	disabled,
	disableText,
	templateOptions,
	templateLabel,
	value = '',
	contact,
	onTracking,
}: SubmitFormProps) {
	const [form] = Form.useForm<FormValues>();
	const [currMessage, setCurrMessage] = useState<string>(''); // used for button disabling
	const [isReminderOn, setIsReminderOn] = useState<boolean>(false);
	const [isSendButtonTooltipOn, setIsSendButtonTooltipOn] = useState<boolean>(false);

	useEffect(() => {
		setCurrMessage(value);
	}, [value]);

	useEffect(() => {
		updateDatePickerField();
	}, [selectedDate]);

	const templateOptionMap = useMemo(
		() => templateOptions && arrayToMap(templateOptions, (template) => template.id),
		[templateOptions]
	);

	function validateAndSubmit(
		event: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLTextAreaElement>
	) {
		event.preventDefault();
		form.validateFields()
			.then((values) => {
				const { message, templateId } = values;
				if (onSubmit) {
					onSubmit(message, templateId);
					form.resetFields();
				}
			})
			.catch((err) => {
				console.log(err);
			});
	}

	function handleFinish(values: FormValues) {
		if (onSubmit) {
			onSubmit(values.message, values.templateId);
			form.resetFields();
		}
		if (onSetReminder && isReminderOn && selectedDate) {
			onSetReminder(selectedDate);
			setIsReminderOn(false);
		}
	}

	function handleTemplateDropdown(templateKey: string) {
		const template = templateOptionMap?.get(templateKey);
		if (!disableText && template) {
			form.setFieldsValue({ message: template.value });
			setCurrMessage(template.value);
			onTracking?.({ buttonClicked: 'Select Click and Send Template' });
		}
	}

	function onTextAreaChange(text: string) {
		setCurrMessage(text);
		if (text === '') {
			form.setFieldsValue({ templateId: undefined });
		}
	}

	function onChangeDate(date: moment.Moment | null) {
		if (onSetDate && !date) {
			// resets date when datepicker is reset
			onSetDate(null);
		}
	}

	function updateDatePickerField() {
		form.setFieldsValue({ dateSelected: selectedDate });
	}

	function handleSendButtonTooltip(visible: boolean) {
		setIsSendButtonTooltipOn(visible && isReminderOn && !selectedDate);
	}

	function handleTriggerReminder(date: Moment) {
		onSetReminderDate?.(date);
		setIsReminderOn(true);
	}

	const redirectToClickAndSendTemplate = useRedirectToTemplate(CampaignDashboardTabKeys.Template);

	const displayDropDownOptions = useMemo(
		() =>
			templateOptions
				? templateOptions.map((t: TemplateOptionType) => (
						<Option value={t.id} key={t.id}>
							{t.label}
						</Option>
				  ))
				: undefined,
		[templateOptions]
	);

	return (
		<Form
			form={form}
			onFinish={handleFinish}
			initialValues={{
				message: value,
			}}
		>
			{!!displayDropDownOptions && (
				<Row>
					<Col>
						<Form.Item
							name="templateId"
							style={{ float: 'left', marginBottom: '10px' }}
						>
							{displayDropDownOptions.length > 0 ? (
								<Select
									onChange={(templateId: string) => {
										handleTemplateDropdown(templateId);
									}}
									style={{ width: '250px' }}
									placeholder={<Text>{templateLabel}</Text>}
									dropdownAlign={{
										points: ['bl', 'tl'],
									}}
									dropdownMatchSelectWidth={false}
									dropdownStyle={{ maxWidth: '400px' }}
								>
									{displayDropDownOptions}
								</Select>
							) : (
								<Button
									onClick={() =>
										redirectToClickAndSendTemplate(
											contact?.campaignIds[0] ?? ''
										)
									}
								>
									Set up Quick Responses
								</Button>
							)}
						</Form.Item>
					</Col>
				</Row>
			)}
			<Row>
				<Col span={24}>
					<Form.Item name="message" style={{ marginBottom: '4px' }}>
						<TextArea
							disabled={disableText}
							onCtrlEnter={validateAndSubmit}
							rows={3}
							style={{ resize: 'none' }}
							autoSize={{ minRows: 3, maxRows: 5 }}
							placeholder={disableText ? 'Sending Message...' : ''}
							onChange={(event) => onTextAreaChange(event.target.value)}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Row
				justify="space-between"
				style={{ display: onSetDate || onSetReminderDate ? undefined : 'block' }}
			>
				{onSetDate && (
					<Col>
						<Form.Item
							name="dateSelected"
							style={{ marginBottom: '4px', float: 'left' }}
						>
							<CopilotDatePicker
								showTime={{ defaultValue: moment('08:00:00', 'HH:mm:ss') }}
								format="MM/DD/YYYY HH:mm:ss"
								placeholder="Select Time"
								onOk={onSetDate}
								onOpenChange={updateDatePickerField}
								onChange={onChangeDate}
								style={{ marginRight: '0.5em', width: '250px' }}
								disabledDate={disablePrevDates}
								showNow={false}
							/>
						</Form.Item>
					</Col>
				)}
				{onSetReminderDate && (
					<Col>
						<Form.Item name="reminderSet">
							<ReminderSelector
								isChecked={isReminderOn}
								onCheck={() => setIsReminderOn((checked) => !checked)}
								onSelectDate={handleTriggerReminder}
								selectedDate={selectedDate ?? undefined}
								dateOptions={ReminderDateOptions}
							/>
						</Form.Item>
					</Col>
				)}
				<Col>
					<Form.Item style={{ float: 'right' }}>
						<Tooltip
							title="Please select a date"
							open={isSendButtonTooltipOn}
							onOpenChange={handleSendButtonTooltip}
							overlayStyle={{ width: '170px' }}
						>
							<Button
								type="primary"
								htmlType="submit"
								disabled={!!disabled || currMessage.trim() === ''}
							>
								{submitText}
							</Button>
						</Tooltip>
					</Form.Item>
				</Col>
			</Row>
		</Form>
	);
}

export default SubmitForm;
